import React, { useState, useEffect } from "react";
import { principalTopics as texts_es } from "../../../utils/text/graphTexts";
import { principalTopics as texts_en } from "../../../utils/text/graphTexts_en";
import { useParams } from "react-router-dom";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from ".././../reusable/CardOnlyYear";
import { getPrincipalTopicsData } from "../../../services/dashboard";
import { generateTreemapTooltip } from "../../../utils/functions/generateTreemapTooltip";
import { getDateString } from "../../../utils/functions/getDateString";
import {
  SMALL_SCREEN_TREEMAP_CONFIG,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { formatNumber } from "../../../utils/functions/formatNumber";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";
import { getMonthText } from "../../../utils/functions/getMonthText";

export default function PrincipalTopics({ selectedCountry }) {
  const { country } = useParams();
  const { dateOptions, topicOptions, subtopicOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("principal-topics");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [selectedQuarter, setSelectedQuarter] = useState();

  // Unique to PrincipalTopics component
  // Never send unique states to Card components
  const [lastMonthDataValue, setLastMonthDataValue] = useState();

  const handleYearChange = (newSelectedYear) => {
    setSelectedYear(newSelectedYear);
  };
  const handleQuarterChange = (newSelectedQuarter) => {
    setSelectedQuarter(newSelectedQuarter);
  };

  const getTopicWithMaxData = (data) => {
    let topicValue = -11;
    let maxTopicFound;
    data.forEach((e) => {
      if (e.value > topicValue) {
        topicValue = e.value;
        maxTopicFound = e;
      }
    });

    return maxTopicFound;
  };

  const getLevelOptions = () => {
    return [
      {
        color: [
          "rgba(48, 94, 169, 1)",
          "rgba(48, 94, 169, 0.95)",
          "rgba(48, 94, 169, 0.90)",
          "rgba(48, 94, 169, 0.80)",
          "rgba(48, 94, 169, 0.72)",
          "rgba(48, 94, 169, 0.65)",
          "rgba(48, 94, 169, 0.54)",
          "rgba(48, 94, 169, 0.45)",
          "rgba(48, 94, 169, 0.35)",
          "rgba(48, 94, 169, 0.30)",
          "rgba(48, 94, 169, 0.26)",
          "rgba(48, 94, 169, 0.20)",
        ],
        colorMapping: "value",
        itemStyle: {
          borderWidth: 0,
          gapWidth: 8,
          borderRadius: 20,
        },
        backgroundColor: "transparent",
      },
      {
        color: [
          "rgba(48, 94, 169, 1)",
          "rgba(48, 94, 169, 0.95)",
          "rgba(48, 94, 169, 0.90)",
          "rgba(48, 94, 169, 0.80)",
          "rgba(48, 94, 169, 0.72)",
          "rgba(48, 94, 169, 0.65)",
          "rgba(48, 94, 169, 0.54)",
          "rgba(48, 94, 169, 0.45)",
          "rgba(48, 94, 169, 0.35)",
          "rgba(48, 94, 169, 0.30)",
          "rgba(48, 94, 169, 0.26)",
          "rgba(48, 94, 169, 0.20)",
        ],
        label: {
          position: "inside",
          fontSize: 16,
          fontFamily: "Open Sans",
          fontWeight: 600,
          formatter: (params) => {
            return (
              params.data.name +
              ": " +
              formatNumber(params.data.value.toFixed(1), language) +
              "%"
            );
          },
          overflow: "truncate",
        },
        itemStyle: {
          borderWidth: 0,
          gapWidth: 8,
          borderRadius: 20,
        },
        backgroundColor: "transparent",
      },
      {
        label: {
          position: "inside",
          color: "white",
          fontSize: 16,
          fontFamily: "Open Sans",
          fontWeight: 600,
          formatter: (params) => {
            return (
              params.data.name +
              ": " +
              formatNumber(params.data.value.toFixed(1), language) +
              "%"
            );
          },
          overflow: "truncate",
        },
        itemStyle: {
          borderWidth: 0,
          gapWidth: 8,
          borderRadius: 20,
        },
      },
    ];
  };

  // Useeffect that collents yead and quarter data
  useEffect(() => {
    if (dateOptions !== null) {
      if (
        dateOptions.mostRecentQuarterDate &&
        topicOptions &&
        subtopicOptions
      ) {
        setSelectedYear(dateOptions.mostRecentQuarterDate.year);
        setSelectedQuarter(dateOptions.mostRecentQuarterDate.quarter);
        setIsLoadingInitialData(false);
      }
      setIsLoadingInitialData(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, topicOptions, subtopicOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    const updateData = async () => {
      try {
        setIsLoading(true);

        const xeno = false;
        const { data } = await getPrincipalTopicsData(
          currentCountry,
          selectedYear,
          selectedQuarter,
          xeno
        );

        // Temporary code to check if no data is available
        let isDataZero = false;
        data.forEach((element) => {
          isDataZero = isDataZero || element.value !== 0;
        });

        if (!isDataZero) {
          setOptions({});
          setIsDataAvailable(false);
          setIsLoading(false);
          return;
        }
        //update "Migratory Policy" to "Migration Policy"
        topicOptions.forEach((item, i) => {
          if(item.code === 'migratory_policy' && language === 'en') topicOptions[i].label = 'Migration Policy'
        })
      
        data.forEach((e) => {
          e.code = e.name;
          e.name = topicOptions.find((topic) => topic.code === e.name).label;
          e.children.forEach((child) => {
            child.code = child.name;
            //update "Migratory Policy" to "Migration Policy"
            child.name = child.code === 'migratory_policy' ? language === 'en' ? 'Migration Policy' : 'Política Migratoria' : subtopicOptions.find(
              (sto) => sto.code.toLowerCase() === e.code + "-" + child.code
            ).label;
          });
        });

        const options = {
          tooltip: {
            trigger: "item",
            ...TOOLTIP_STYLE,
          },
          backgroundColor: "transparent",
          series: {
            ...SMALL_SCREEN_TREEMAP_CONFIG,
            name: language === "en" ? "Principal Topics" : "Temas Principales",
            type: "treemap",
            leafDepth: 1,
            roam: false,
            levels: getLevelOptions(),
            drillDownIcon: "",
            label: {
              fontFamily: "Open Sans",
              color: "white",
            },
            tooltip: {
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                params.lang = language;
                return generateTreemapTooltip(params);
              },
            },
            itemStyle: {
              borderColor: "transparent",
            },
            // labelLayout: function (params) {
            //   return params.dataIndex
            //     ? {
            //         fontSize: Math.min(Math.max(params.rect.width / 10, 14), 35),
            //       }
            //     : {
            //         fontSize: 14,
            //       };
            // },
            data: data,
          },
        };

        // set data in description
        const maxTopic = getTopicWithMaxData(data);
        setLastMonthData(maxTopic.name);
        setLastMonthDataValue(maxTopic.value.toFixed(1));

        setOptions(options);
        setIsDataAvailable(true);
        setIsLoading(false);
      } catch (error) {
        setErrorLoadingGraph(true);
        setLastMonthData("--");
        setLastMonthDataValue("--");
        setOptions(null);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    };

    if (
      !isLoadingInitialData &&
      dateOptions &&
      selectedYear &&
      selectedQuarter &&
      currentCountry
    )
      updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    topicOptions,
    subtopicOptions,
    selectedYear,
    selectedQuarter,
    isLoadingInitialData,
    currentCountry,
  ]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
  }, [language]);

  function PrincipalTopicsCardContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          lastMonthData={lastMonthData}
          dataDate={getDateString(
            language,
            selectedYear,
            selectedQuarter === "ALL" ? null : selectedQuarter
          )}
          cardTitle={texts.boxTitle(false)}
          methodology={texts.methodology(false)}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            false,
            selectedYear,
            selectedQuarter,
            lastMonthData,
            lastMonthDataValue
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: handleYearChange,
              options: dateOptions.yearOptions.filter(
                (year) => year.years_code !== "HIST"
              ),
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
            {
              selectedValue: selectedQuarter,
              changeHandler: handleQuarterChange,
              options: dateOptions.quarterOptions[selectedYear],
              placeHolderText: miscText["quarterPlaceholder"][language],
              labelAttributeName: "label",
              valueAttributeName: "value",
              ignoreTranslation: true,
            },
          ]}
          language={language}
          includesGlossary={true}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "TemasPrincipales" : "PrincipalTopics",
            currentCountry,
            selectedYear,
            selectedQuarter
          )}
          downloadableCardTitle={texts.boxTitle(false)}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language,
            getMonthText(selectedQuarter, language)
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle(false)}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description()}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
            {
              placeHolderText: miscText["quarterPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <PrincipalTopicsCardContent />}
    </CardContainer>
  );
}
